import { makeAutoObservable, autorun } from "mobx";
import { format, parseISO } from "date-fns";
import { request } from "../utils";
import { showErrorToast, showSuccessToast } from "../services/ToastService";
import AuthStore from "./AuthStore";
import PeopleStore from "./PeopleStore";
import ClassesStore from "./ClassesStore";
import CurriculumStore from "./CurriculumStore";
import MaterialsStore from "./MaterialsStore";
import RegistrationService from "../services/RegistrationService";

class RegistrationsStore {
  constructor() {
    makeAutoObservable(this);

    autorun(() => {
      if (AuthStore.authenticated) this.init();
      else this.clear();
    });
  }

  loading = false;
  initialized = false;
  initializing = false;

  rawRecentRegistrations = [];

  get recentRegistrations() {
    const groupedToParents = Object.values(
      this.rawRecentRegistrations?.reduce((acc, next) => {
        const { class: classItem, parent, registrationDate } = next || {};
        const classId = classItem?.id || classItem?.classId;
        const registrationId = `${classId}-${parent?.id}-${registrationDate}`;

        const { student, ...registration } = next || {};
        acc[registrationId] = { ...registration, students: (acc[registrationId]?.students || []).concat(student) };
        return acc;
      }, {})
    );
    return groupedToParents?.map(r => {
      return {
        ...r,
        class: {
          ...r?.class,
          course: CurriculumStore?.coursesById?.[r?.class?.courseId]
        }
      };
    });
  }

  get registrationsToday() {
    const today = format(new Date(), "yyyy-MM-dd");
    const registrationsToday = this.rawRecentRegistrations?.filter(
      r => format(parseISO(r?.registrationDate), "yyyy-MM-dd") === today
    )?.length;
    return registrationsToday;
  }

  get registrationsByUserId() {
    const currentDateString = new Date().toISOString().slice(0, 10);
    const teacherClassEntries = PeopleStore?.people?.map(({ id: userId, registrations }) => [
      userId,
      registrations
        ?.map(r => ({
          ...r,
          class: {
            ...r?.class,
            ...ClassesStore?.classesById?.[r?.class?.classId],
            course: CurriculumStore?.coursesById?.[r?.class?.courseId]
          }
        }))
        ?.map(r => ({
          ...r,
          current: !!r?.class?.isActive,
          past: !r?.class?.isActive && r?.class?.endDate < currentDateString,
          future: !r?.class?.isActive && r?.class?.startDate > currentDateString
        }))
    ]);
    return Object.fromEntries(teacherClassEntries);
  }

  async init() {
    // Defensive check - prevent re-fetching if already initialized or initializing
    if (this.initialized || this.initializing) return;

    this.initializing = true;
    this.loading = true;

    try {
      await this.fetchRecentRegistrations();
      this.initialized = true;
    } catch (err) {
      console.warn(err);
    }

    this.loading = false;
    this.initializing = false;
  }

  async fetchRecentRegistrations() {
    try {
      const recentRegistrations = await request.get(`/registrations/recent`);
      this.rawRecentRegistrations = recentRegistrations;
    } catch (err) {
      console.warn(err);
    }
  }

  async reassignStudents(reassignParams) {
    const {
      oldTeacherId,
      oldClassId,
      newTeacherId,
      newClassId,
      parentId,
      studentIds,
      itemsToTransfer,
      isCourseChange
    } = reassignParams || {};
    try {
      await request.put(`/registrations/reassign`, {
        body: {
          oldTeacherId,
          oldClassId,
          newTeacherId,
          newClassId,
          parentId,
          studentIds,
          itemsToTransfer,
          isCourseChange
        }
      });
      showSuccessToast("Successfully reassigned students.");
      await Promise.all([
        ClassesStore.fetchStudentsForClassById(oldClassId),
        ClassesStore.fetchStudentsForClassById(newClassId),
        PeopleStore.fetchPersonById(oldTeacherId),
        PeopleStore.fetchPersonById(newTeacherId)
      ]);
    } catch (err) {
      console.warn("Error reassigning students:", err);
      showErrorToast("Error reassigning students.");
    }
  }

  async dropStudents({ teacherId, classId, parentId, studentIds, itemsToRemove }) {
    try {
      await request.put(`/registrations/drop`, { body: { teacherId, classId, parentId, studentIds, itemsToRemove } });
      showSuccessToast("Successfully dropped students.");
      await Promise.all([
        ClassesStore.fetchStudentsForClassById(classId),
        PeopleStore.fetchPersonById(teacherId),
        ClassesStore.fetchDroppedStudentsForClassById(classId)
      ]);
    } catch (err) {
      console.warn("Error dropping students:", err);
      showErrorToast("Error dropping students.");
    }
  }

  async undropStudents({ teacherId, classId, parentId, studentIds }) {
    try {
      await request.put(`/registrations/undrop`, { body: { teacherId, classId, parentId, studentIds } });
      showSuccessToast("Successfully undropped students.");
      await Promise.all([
        ClassesStore.fetchStudentsForClassById(classId),
        PeopleStore.fetchPersonById(teacherId),
        ClassesStore.fetchDroppedStudentsForClassById(classId)
      ]);
    } catch (err) {
      console.warn("Error undropping students:", err);
      showErrorToast("Error undropping students.");
    }
  }

  async addManualRegistration(params) {
    const {
      selectedTeacher,
      selectedClass,
      selectedParent,
      selectedChildren,
      studentBundles,
      extras,
      card,
      noProductsToShip
    } = params || {};

    try {
      // Build parent object with additional fields for V2 API
      const parent = {
        id: selectedParent?.id,
        infusionsoftId: selectedParent?.infusionsoftId,
        firstName: selectedParent?.firstName || undefined,
        lastName: selectedParent?.lastName || undefined,
        email: selectedParent?.email?.[0]?.email || undefined
      };

      // Build students array with additional fields
      const students = selectedChildren?.map(student => ({
        id: student?.id,
        infusionsoftId: student?.infusionsoftId,
        firstName: student?.firstName || undefined,
        lastName: student?.lastName || undefined
      }));

      // Build teacher object with additional fields
      const teacher = {
        id: selectedTeacher?.id,
        infusionsoftId: selectedTeacher?.infusionsoftId,
        firstName: selectedTeacher?.firstName || undefined,
        lastName: selectedTeacher?.lastName || undefined
      };

      // Build class object with additional fields
      const classObject = {
        id: selectedClass?.id || selectedClass?.classId,
        name: selectedClass?.name || undefined,
        courseId: selectedClass?.courseId,
        teacherId: selectedTeacher?.id || undefined
      };

      const simplifiedProducts =
        Object.entries(extras)
          ?.filter(([id]) => !MaterialsStore?.shippingProductIds?.includes(id))
          ?.map(([id, quantity]) => ({ id, quantity })) || [];

      const simplifiedProductsFromBundles =
        Object.values(studentBundles)
          ?.map(b =>
            b?.productList
              ?.filter(id => !MaterialsStore?.shippingProductIds?.includes(id))
              ?.map(id => ({ id, quantity: 1 }))
          )
          ?.flat() || [];

      const shippingItemForCourse = MaterialsStore?.shippingItemsByCourseId?.[classObject?.courseId];
      const simplifiedShippingProduct = shippingItemForCourse?.id ? { id: shippingItemForCourse?.id, quantity: 1 } : null;

      const combinedProductQuantitiesById = simplifiedProducts
        ?.concat(simplifiedProductsFromBundles)
        ?.concat(simplifiedShippingProduct)
        ?.filter(Boolean)
        ?.filter(product => product?.id && product?.id !== 'undefined') // Filter out products with undefined IDs
        ?.reduce((acc, next) => {
          acc[next?.id] = (acc[next?.id] || 0) + next?.quantity;
          return acc;
        }, {});
      const products = Object.entries(combinedProductQuantitiesById)
        ?.map(([id, quantity]) => ({ id, quantity }))
        ?.filter(product => product?.id && product?.id !== 'undefined'); // Double-check to ensure no undefined IDs

      // Build the request payload for V2 API
      const requestPayload = {
        registrations: [{
          parent,
          students,
          teacher,
          class: classObject,
          products,
          noProductsToShip,
          promoCodeId: null  // Admin doesn't use promo codes
        }],
        card: {
          id: card?.id,
          token: undefined,
          last4: undefined,
          brand: undefined
        }
      };

      console.log('[RegistrationsStore] Submitting manual registration with V2 API');

      // Use RegistrationService to handle the V2 API call with idempotency
      const result = await RegistrationService.submitRegistration(requestPayload);

      // Get the idempotency key that was used
      const registrationState = RegistrationService.getState();
      console.log('[RegistrationsStore] Registration submitted with idempotency key:', {
        idempotencyKey: registrationState.idempotencyKey,
        transactionId: result.transactionId,
        success: result.success,
        cached: result.cached
      });

      if (result.success) {
        // Show appropriate success message
        if (result.cached || result.isDuplicate) {
          showSuccessToast("This enrollment was already processed successfully.");
        } else {
          showSuccessToast("Successfully created enrollment.");
        }
        return {
          success: true,
          transactionId: result.transactionId,
          cached: result.cached,
          isDuplicate: result.isDuplicate
        };
      }

      // Handle error response from V2 API
      const errorMessage = result.userMessage || result.message || result.error;

      // Show appropriate error toast based on error type
      if (result.errorType === 'PAYMENT_ERROR') {
        showErrorToast("Looks like there was an issue processing the card.");
      } else if (result.errorType === 'CAPACITY_ERROR') {
        showErrorToast("This class is full.");
      } else if (result.errorType === 'VALIDATION_ERROR') {
        showErrorToast("Please check your inputs and try again.");
      } else {
        showErrorToast("Error creating enrollment.");
      }

      return {
        success: false,
        error: errorMessage,
        errorType: result.errorType,
        canRetry: result.canRetry || false
      };
    } catch (err) {
      console.error('[RegistrationsStore] Unexpected error:', err);
      let error;
      const { message } = err?.response?.data || {};
      if (message === "Error processing payment") {
        showErrorToast("Looks like there was an issue processing the card.");
        error =
          "Looks like there was an issue processing the card. Check your inputs and try again, or use another card.";
      } else {
        showErrorToast("Error creating enrollment.");
        error = "Looks like something went wrong. Reach out to T&T and we'll get things sorted.";
      }

      return { success: false, error, canRetry: true };
    }
  }

  clear() {
    this.rawRecentRegistrations = [];
    this.loading = false;
    this.initialized = false;
    this.initializing = false;
  }
}

export default new RegistrationsStore();
